import { createAsyncThunk, createDraftSafeSelector, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import BigNumber from "bignumber.js";
import { BASE18, BASE9, FS } from "src/configs/constance";
import { isNullAddress, shuffleArray } from "src/utils";
import multiCall from "src/utils/multicall";
import NFTTicketContract from "src/contract-logic/logic/NFTTicketContract";
import NFTTicketCounterContract from "src/contract-logic/logic/NFTTicketCounterContract";
import NFTTicketMarketplaceContract from "src/contract-logic/logic/NFTTicketMarketplaceContract";
import NFTTicketStaking from "src/contract-logic/logic/NFTTicketStaking";

const client = axios.create({ baseURL: "https://scoringapi.trava.finance/ticket-nft" });

export const TICKET_TO_ID = { counter: 100001, presale: 100002, incentive: 100003 };
export const ID_TO_TICKET = { 100001: "counter", 100002: "presale", 100003: "incentive" };
export const TICKET_IDS = [100001, 100002, 100003];

const initialState = {
  fetchTicketSellingStatus: FS.INITIAL,
  incentiveCounter: undefined,
  counterInfo: undefined,
  roundInfo: undefined,
  counterParams: undefined,
  ticketPriceHistories: [],
  ticketDetail: undefined,
  slotCount: undefined,
  slotPricePerHour: undefined,
  topTicket: undefined,
  ticket: undefined,
  totalTicketStaking: undefined,
  userStakingTotal: undefined,
  unlockStakingTime: undefined,
  stakingTime: undefined,
  finishTimestamp: undefined,
  stakingEarned: undefined,
};

export const getPriceTicketHistories = createAsyncThunk(
  "ticketSlice/getPriceTicketHistories",
  async ({ timeframe }) => {
    const times = {
      "5m": 300,
      "15m": 900,
      "30m": 1800,
      "1h": 3600,
      "2h": 7200,
      "4h": 14400,
      "1d": 86400,
      "1w": 604800,
      "1m": 2419200,
      "3m": 7257600,
    };
    const start_timestamp = (new Date().getTime() / 1000 - times[timeframe]).toFixed(0);
    const res = await client.get("/tickets/price-history", { params: { start_timestamp } }).then((res) => res.data);
    if (res.price_history) {
      return Object.keys(res.price_history).map((key) => [parseInt(key) * 1000, res.price_history[key]]);
    } else {
      return [];
    }
  }
);

export const getRoundData = createAsyncThunk("ticketSlice/getRoundData", async (_) => {
  try {
    const [roundInfo, currentRoundRemainingTime, config, userBuyLimit, userSellLimit, bonding] = await Promise.all([
      NFTTicketCounterContract.getRoundData(),
      NFTTicketCounterContract.getCurrentRoundRemainingTime(),
      NFTTicketCounterContract.getRoundConfig(),
      NFTTicketCounterContract.getUserBuyLimit(),
      NFTTicketCounterContract.getUserSellLimit(),
      NFTTicketCounterContract.getBonding(),
    ]);
    const indexes = await NFTTicketCounterContract.getRoundIndexes(parseInt(roundInfo[1]));
    return {
      currentRound: parseInt(roundInfo[1]),
      isActive: currentRoundRemainingTime[0],
      remainingTime: parseInt(currentRoundRemainingTime[1]) * 1000,
      desTime: Date.now() + parseInt(currentRoundRemainingTime[1]) * 1000,
      remainingBuyTicket: parseInt(config.sellLimit) - parseInt(indexes.ticketSold),
      remainingSellTicket: parseInt(config.buyBackLimit) - parseInt(indexes.ticketBoughtBack),
      sellLimit: parseInt(config.sellLimit),
      userSellLimit,
      userBuyLimit,
      maxBuy: BigNumber(bonding.maxBuy).dividedBy(BASE9).toNumber(),
    };
  } catch (error) {
    console.error(error);
  }
});

export const getCounterParameter = createAsyncThunk("ticketSlice/getCounterParameter", async (_) => {
  const counterParams = await NFTTicketCounterContract.getCounterParameter();
  return {
    activePhaseInterval: parseInt(counterParams.activePhaseInterval),
    inactivePhaseInterval: parseInt(counterParams.inactivePhaseInterval),
  };
});

export const getIncentiveCounter = createAsyncThunk("ticketSlice/getIncentiveCounter", async (_) => {
  const [price, limit] = await Promise.all([
    NFTTicketCounterContract.getIncentiveCounterPrice(),
    NFTTicketCounterContract.getIncentiveCounterLimit(),
  ]);
  return {
    limit,
    price: BigNumber(price).dividedBy(BASE18).toNumber(),
  };
});

export const getCounterBuyingPrice = createAsyncThunk("ticketSlice/getCounterBuyingPrice", async () => {
  const price = await NFTTicketCounterContract.getCounterBuyingPrice();
  return { price: BigNumber(price).dividedBy(BASE18).toNumber() };
});

export const getSlotCount = createAsyncThunk("ticketSlice/getSlotCount", async () => {
  const res = await NFTTicketMarketplaceContract.getSlotCount();
  return res;
});

export const fetchTopTicketSelling = createAsyncThunk("ticketSlice/fetchTopTicketSelling", async () => {
  const [sponsorQueue] = await Promise.all([
    multiCall(
      NFTTicketMarketplaceContract.ABI,
      new Array(8).fill(1).map((_, index) => ({
        address: NFTTicketMarketplaceContract.ADDRESS,
        name: "getSponsorQueueOfSlot",
        params: [index + 1],
      }))
    ),
  ]);
  const sponsorQueueFlattened = sponsorQueue.flat();
  const [sponsorQueueValid] = await Promise.all([
    multiCall(
      NFTTicketMarketplaceContract.ABI,
      sponsorQueueFlattened.map((item) => ({
        address: NFTTicketMarketplaceContract.ADDRESS,
        name: "isSponsorQueueValid",
        params: [parseInt(item)],
      }))
    ),
  ]);
  const sponsorQueueValidFlattened = sponsorQueueValid.flat();
  const [sponsorQueueInfo] = await Promise.all([
    multiCall(
      NFTTicketMarketplaceContract.ABI,
      sponsorQueueFlattened
        .filter((_, index) => sponsorQueueValidFlattened[index])
        .map((slot) => ({
          address: NFTTicketMarketplaceContract.ADDRESS,
          name: "getSponsorQueueInfo",
          params: [parseInt(slot)],
        }))
    ),
  ]);
  const sponsorQueueInfoFlattened = sponsorQueueInfo.flat();
  const [orderInfo] = await Promise.all([
    multiCall(
      NFTTicketMarketplaceContract.ABI,
      sponsorQueueInfoFlattened.map((info) => ({
        address: NFTTicketMarketplaceContract.ADDRESS,
        name: "getSellOrderInfo",
        params: [parseInt(info.sellOrderId)],
      }))
    ),
  ]);
  const orderInfoFlattened = orderInfo.flat();
  const res = orderInfoFlattened.map((item, idx) => {
    const seller = item.seller;
    const price = BigNumber(item.price._hex).dividedBy(BASE18).toNumber();
    const tokenId = parseInt(item.tokenId);
    const stock = parseInt(item.stock);
    const id = parseInt(sponsorQueueInfoFlattened[idx].sellOrderId);
    return {
      seller,
      price,
      tokenId,
      stock,
      orderId: id,
      paymentOption: parseInt(item.paymentOption),
      sponsorQueue: parseInt(item.sponsorQueue),
    };
  });
  return res;
});

export const fetchTicketSellingList = createAsyncThunk("ticketSlice/fetchTicketSellingList", async (_, thunkAPI) => {
  const total = await NFTTicketMarketplaceContract.getAllTicketSellOrderCount();
  thunkAPI.dispatch(updateTotalTicketSellingNFTs({ total }));
  const [orderId] = await Promise.all([
    multiCall(
      NFTTicketMarketplaceContract.ABI,
      new Array(total).fill(1).map((_, idx) => ({
        address: NFTTicketMarketplaceContract.ADDRESS,
        name: "getAllSellOrderAtIndex",
        params: [idx],
      }))
    ),
  ]);
  const orderIdsFlattened = orderId.flat();
  shuffleArray(orderIdsFlattened);

  let chunk = 500,
    orderIdListChunkPromises = [],
    n = orderIdsFlattened.length;
  for (let i = 0; i < n; i += chunk) {
    let orderIdSlice = orderIdsFlattened.slice(i, i + chunk);
    orderIdListChunkPromises.push(thunkAPI.dispatch(fetchBatchTravaNFTSellingTicket({ orderIdSlice })));
  }
  await Promise.all(orderIdListChunkPromises);
});

export const fetchBatchTravaNFTSellingTicket = createAsyncThunk(
  "ticketSlice/fetchBatchTravaNFTSellingTicket",
  async ({ orderIdSlice }) => {
    const [orderInfo] = await Promise.all([
      multiCall(
        NFTTicketMarketplaceContract.ABI,
        orderIdSlice.map((orderId) => ({
          address: NFTTicketMarketplaceContract.ADDRESS,
          name: "getSellOrderInfo",
          params: [orderId],
        }))
      ),
    ]);
    const orderInfoFlattened = orderInfo.flat();

    const [sponsorQueueValid] = await Promise.all([
      multiCall(
        NFTTicketMarketplaceContract.ABI,
        orderInfoFlattened.map((item) => ({
          address: NFTTicketMarketplaceContract.ADDRESS,
          name: "isSponsorQueueValid",
          params: [parseInt(item.sponsorQueue)],
        }))
      ),
    ]);
    const sponsorQueueValidFlattend = sponsorQueueValid.flat();
    orderIdSlice = orderIdSlice.filter((_, index) => !sponsorQueueValidFlattend[index]);

    const res = orderInfoFlattened
      .filter((_, index) => !sponsorQueueValidFlattend[index])
      .map((item, idx) => {
        const seller = item.seller;
        const price = BigNumber(item.price._hex).dividedBy(BASE18).toNumber();
        const tokenId = parseInt(item.tokenId);
        const stock = parseInt(item.stock);
        const id = parseInt(orderIdSlice[idx]);
        return {
          seller,
          price,
          tokenId,
          stock,
          orderId: id,
          paymentOption: parseInt(item.paymentOption),
          sponsorQueue: parseInt(item.sponsorQueue),
        };
      })
      .filter((item) => TICKET_IDS.includes(item.tokenId));
    return res;
  }
);
export const getSlotRentPricePerHour = createAsyncThunk("ticketSlice/getSlotRentPricePerHour", async () => {
  const res = await NFTTicketMarketplaceContract.getSlotRentPricePerHour();
  return res;
});

export const fetchTravaNFTSellingTicketDetail = createAsyncThunk(
  "ticketSlice/fetchTravaNFTSellingTicketDetail",
  async ({ orderId }) => {
    const orderInfo = await NFTTicketMarketplaceContract.getSellOrderInfo(orderId);
    if (isNullAddress(orderInfo.seller)) throw new Error("Item not found");

    return {
      price: BigNumber(orderInfo.price).dividedBy(BASE18).toNumber(),
      seller: orderInfo.seller,
      stock: parseInt(orderInfo.stock),
      tokenId: parseInt(orderInfo.tokenId),
      orderId: parseInt(orderId),
      paymentOption: parseInt(orderInfo.paymentOption),
    };
  }
);

export const getTimeStaking = createAsyncThunk("ticketSlice/getTimeStaking", async () => {
  const [stakingTime, unlockStakingTime] = await Promise.all([
    NFTTicketStaking.getStakingTime(),
    NFTTicketStaking.getUnlockTime(),
  ]);
  let finishTimestamp = undefined;
  if (stakingTime) {
    finishTimestamp = unlockStakingTime * 1000 + stakingTime * 1000;
  }
  return { stakingTime, unlockStakingTime, finishTimestamp };
});

export const getInfoStaking = createAsyncThunk("ticketSlice/getInfoStaking", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const [totalTicketStaking, userStakingTotal, stakingEarned] = await Promise.all([
    NFTTicketContract.getBalance(NFTTicketStaking.ADDRESS, TICKET_TO_ID.presale),
    NFTTicketStaking.getUserStakingTotal(accountAddress),
    NFTTicketStaking.getTotalUserReward(accountAddress),
  ]);
  return { totalTicketStaking, userStakingTotal, stakingEarned };
});

const ticketSlice = createSlice({
  name: "ticketSlice",
  initialState,
  reducers: {
    resetMarketplaceTicket(state) {
      if (state.ticket?.list) state.ticket = undefined;
    },
    updateTotalTicketSellingNFTs(state, action) {
      state.ticket = { ...state.ticket, ...action.payload };
    },
    removeTicketDetail(state, _) {
      state.ticketDetail = undefined;
    },
    updateTicketMarketplace(state, action) {
      const orderId = action.payload.id;
      const slot = action.payload.slot;
      const ticketList = state.ticket?.list;
      if (ticketList) {
        const index = ticketList.findIndex((item) => item.orderId === parseInt(orderId));
        if (index < 0) return state;
        state.ticket.list[index].sponsorQueue = slot;
      }
    },
    removeSellingTicketOnMarket(state, action) {
      const orderId = action.payload.orderId;
      const tick = action.payload.tick;
      const ticketList = state.ticket?.list;
      if (ticketList) {
        const index = ticketList.findIndex((item) => item.orderId === parseInt(orderId));
        if (index < 0) return state;
        if (tick >= state.ticket.list[index].stock) {
          state.ticket.list = ticketList.slice(0, index).concat(ticketList.slice(index + 1));
          if (state.ticket?.total) state.ticket.total -= 1;
        } else state.ticket.list[index].stock -= tick;
      }
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(getIncentiveCounter.fulfilled, (state, action) => {
        state.incentiveCounter = action.payload;
      })
      .addCase(getCounterBuyingPrice.fulfilled, (state, action) => {
        state.counterInfo = action.payload;
      })
      .addCase(getRoundData.fulfilled, (state, action) => {
        state.roundInfo = action.payload;
      })
      .addCase(getSlotCount.fulfilled, (state, action) => {
        state.slotCount = action.payload;
      })
      .addCase(getSlotRentPricePerHour.fulfilled, (state, action) => {
        state.slotPricePerHour = action.payload;
      })
      .addCase(getCounterParameter.fulfilled, (state, action) => {
        state.counterParams = action.payload;
      })
      .addCase(getPriceTicketHistories.fulfilled, (state, action) => {
        state.ticketPriceHistories = action.payload;
      })
      .addCase(fetchTicketSellingList.fulfilled, (state) => {
        state.fetchTicketSellingStatus = FS.SUCCESS;
      })
      .addCase(fetchBatchTravaNFTSellingTicket.fulfilled, (state, action) => {
        if (!state.ticket.list) {
          state.ticket.list = action.payload;
        } else {
          state.ticket.list = state.ticket.list.concat(action.payload);
        }
      })
      .addCase(fetchBatchTravaNFTSellingTicket.rejected, (state, action) => {
        state.fetchTicketSellingStatus = FS.FAIL;
        console.error(action);
      })
      .addCase(fetchTravaNFTSellingTicketDetail.fulfilled, (state, action) => {
        state.ticketDetail = action.payload;
      })
      .addCase(fetchTravaNFTSellingTicketDetail.rejected, (state, action) => {
        state.ticketDetail = undefined;
        console.error(action);
      })
      .addCase(fetchTopTicketSelling.fulfilled, (state, action) => {
        state.topTicket = action.payload;
      })
      .addCase(fetchTopTicketSelling.rejected, (state, action) => {
        console.error(action.error);
      })
      .addCase(getTimeStaking.fulfilled, (state, action) => {
        state.unlockStakingTime = action.payload.unlockStakingTime;
        state.stakingTime = action.payload.stakingTime;
        state.finishTimestamp = action.payload.finishTimestamp;
      })
      .addCase(getInfoStaking.fulfilled, (state, action) => {
        state.totalTicketStaking = action.payload.totalTicketStaking;
        state.userStakingTotal = action.payload.userStakingTotal;
        state.stakingEarned = action.payload.stakingEarned;
      }),
});

export default ticketSlice.reducer;

export const {
  updateTimer,
  resetMarketplaceTicket,
  updateTicketMarketplace,
  updateTotalTicketSellingNFTs,
  removeSellingTicketOnMarket,
  removeTicketDetail,
} = ticketSlice.actions;

export const selectTicketSlice = (state) => state.ticketSlice;

export const selectNFTSellingTicketInMarket = createDraftSafeSelector(
  (state) => selectTicketSlice(state).ticket,
  (ticket) => {
    if (!ticket) return null;
    const temp = ticket.list || [];
    return temp;
  }
);

export const selectNFTTopSellingTicketInMarket = createDraftSafeSelector(
  (state) => selectTicketSlice(state).topTicket,
  (ticket) => {
    if (!ticket) return null;
    const temp = ticket || [];
    return temp;
  }
);
